/**
 * Customers List Page
 *
 * Clara Vale stat cards + DataTable.
 * Shows consumers who have registrations in the current organisation.
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  DataTable,
  ColumnToggle,
  type DataTableColumn,
} from '@/admin/components/data-table'

import { Users } from 'lucide-react'
import { formatPhoneDisplay } from '@/shared/lib/forms'
import { SearchInput } from '@/shared/components/ui/search-input'
import { EmptyState } from '@/admin/components/shared/empty-state'
import api from '@/admin/lib/api'
import { useFormatters } from '@/shared/lib/formatting'

interface CustomerRow {
  uuid: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  registration_count: number
  last_registration: string | null
  created_at: string
}

interface CustomerStatCards {
  total: number
}

export function CustomersPage() {
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const [search, setSearch] = useState('')
  const [customers, setCustomers] = useState<CustomerRow[]>([])
  const [statCards, setStatCards] = useState<CustomerStatCards | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [isLoading, setIsLoading] = useState(true)
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true)
    try {
      const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
      }
      if (search) params.search = search
      if (sorting) params.ordering = `${sorting.direction === 'desc' ? '-' : ''}${sorting.field}`

      const res = await api.get('/registrations/consumers/', { params })
      setCustomers(res.data?.results ?? [])
      setTotalCount(res.data?.count ?? 0)
      setStatCards(res.data?.stat_cards ?? null)
    } catch {
      setCustomers([])
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, search, sorting])

  useEffect(() => {
    fetchCustomers()
  }, [fetchCustomers])

  const columns: DataTableColumn<CustomerRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        sortField: 'first_name',
        width: '22%',
        cell: (row) => (
          <span className="text-[13px] font-normal text-[var(--ink)]">
            {row.first_name} {row.last_name}
          </span>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        sortable: true,
        sortField: 'email',
        width: '24%',
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-2)]">
            {row.email}
          </span>
        ),
      },
      {
        id: 'phone',
        header: 'Phone',
        width: '14%',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-2)]">
            {formatPhoneDisplay(row.phone_number) || '-'}
          </span>
        ),
      },
      {
        id: 'registrations',
        header: 'Registrations',
        width: '12%',
        cell: (row) => (
          <span className="block text-center text-[12px] text-[var(--ink-2)]">
            {row.registration_count}
          </span>
        ),
      },
      {
        id: 'last_registration',
        header: 'Last Registration',
        sortable: true,
        sortField: 'last_registration',
        width: '14%',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {row.last_registration ? formatDate(row.last_registration) : '-'}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Joined',
        sortable: true,
        sortField: 'created_at',
        width: '12%',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
    ],
    [formatDate],
  )

  return (
    <div className="mx-auto max-w-[1100px]">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[13px] text-[var(--ink-3)]">
          End customers with registered products
        </p>
      </div>
      {/* Stat Cards */}
      <div className="mb-6 flex flex-wrap gap-2">
        <div
          className="max-w-40 min-w-[100px] flex-1 rounded-[8px] border-l-2 border-l-[var(--green)] bg-[var(--bg-subtle)] px-4 py-[14px]"
        >
          <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Total
          </p>
          <p className="text-[28px] leading-none font-display text-[var(--ink)]">
            {statCards?.total ?? 0}
          </p>
        </div>
      </div>
      {/* Toolbar */}
      <div
        className="mb-6 flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] pb-6"
      >
        <div className="max-w-xs">
          <SearchInput
            data-testid="customers-search"
            placeholder="Search customers..."
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            className="text-[13px]"
          />
        </div>
        <div className="flex-1" />
        <ColumnToggle
          columns={columns}
          visibility={columnVisibility}
          onVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
        />
      </div>
      {/* Table */}
      <DataTable<CustomerRow>
        tableId="customers"
        columns={columns}
        data={customers}
        totalCount={totalCount}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={() => {}}
        sorting={sorting}
        columnOrder={columnOrder}
        onSortingChange={setSorting}
        onRowClick={(row) => navigate(`/customers/${row.uuid}`)}
        rowTestId={(row) => `customers-item-${row.uuid}`}
        emptyStateNode={<EmptyState icon={Users} title="No customers yet" description="Customers appear here when they register a product." />}
        columnVisibility={columnVisibility}
      />
    </div>
  );
}
